/**
 * The objective catalogue for bundle 2.0.0 (ADR-011).
 *
 * Seventy-nine objectives over seven strands, each one a band of levels inside a game. This file
 * is the teacher-facing vocabulary of the whole product: it is what appears on the assign
 * screen, what a class grid has rows for, and what the recommender walks through.
 *
 * **Two rules held throughout.**
 *
 * *A band is one teachable idea, not a fixed number of levels.* "Adding to ten" is two levels
 * because the same idea is practised with objects and then with digits; "reversing a sequence"
 * is three because the idea is one thing and the levels only lengthen it. Slicing every game
 * into equal thirds would have produced tidy code and objectives a teacher could not name.
 *
 * *`requires` is a suggestion order, never a lock.* A teacher can assign anything to anyone.
 * The chains here say what usually comes first, which is all the recommender needs and all we
 * are entitled to claim about a class we have never met.
 *
 * Persian names live in `apps/web/src/copy.ts`, like every other string a human reads.
 */
const num = (id, from, to, requires = []) => ({
    id,
    strand: "number",
    steps: [{ taskId: "math-arith-bare", from, to }],
    requires,
});
/**
 * Number — counting, then adding, then taking away, then grouping.
 *
 * The one strand a preschool teacher would have asked for unprompted, and the longest chain
 * here: twelve objectives up one game's fourteen levels. The chain is deliberately linear,
 * because arithmetic is the one area where the order genuinely is not negotiable — a child who
 * cannot count to five cannot be taught to add to five, and no teacher would try.
 *
 * **Multiplication and division are at the end, and most children in a preschool class will
 * never be offered them.** That is the design working rather than failing: the recommender only
 * suggests an objective whose predecessors are mastered, so the ladder simply runs out above
 * each child. It is there for the seven-year-old sitting in the same room.
 */
const NUMBER = [
    num("count-to-3", 1, 1),
    num("count-to-5", 2, 2, ["count-to-3"]),
    // Recognising four things AS four, without counting them one by one. A real and separate
    // step, and the one that makes addition possible without finger-counting every time.
    num("count-at-a-glance", 3, 3, ["count-to-5"]),
    /**
     * Counting to ten and to twenty, which the ladder used to skip entirely.
     *
     * It went from five straight to addition — a year short of where preschool counting ends. These are
     * where most of a preschool year is actually spent, so they are two objectives rather than one: the
     * teens are a separate battle from the first ten, and a teacher wants to hand them out separately.
     */
    num("count-to-10", 4, 4, ["count-at-a-glance"]),
    num("count-to-20", 5, 5, ["count-to-10"]),
    num("add-to-5-objects", 6, 6, ["count-to-10"]),
    num("add-to-5-digits", 7, 7, ["add-to-5-objects"]),
    num("add-to-10", 8, 9, ["add-to-5-digits"]),
    num("subtract-to-5", 10, 10, ["add-to-5-digits"]),
    num("subtract-to-10", 11, 11, ["subtract-to-5", "add-to-10"]),
    num("add-subtract-mixed", 12, 12, ["subtract-to-10"]),
    num("add-to-20", 13, 13, ["add-subtract-mixed", "count-to-20"]),
    // Multiplication as equal groups before multiplication as a fact to recall.
    num("multiply-groups", 14, 14, ["add-subtract-mixed"]),
    num("multiply", 15, 15, ["multiply-groups"]),
    num("divide", 16, 16, ["multiply"]),
];
/**
 * Ordering by size — seriation, which is a number idea before it is a number.
 *
 * Filed under number rather than space because what it teaches is ordinality: that things have
 * a place in a sequence and the place is comparative. A child who can order five sticks by
 * length has the idea that three comes between two and four.
 */
const ORDERING = [
    {
        id: "order-by-size",
        strand: "number",
        steps: [{ taskId: "vs-ordering-bare", from: 1, to: 4 }],
        requires: [],
    },
    {
        id: "order-by-size-close",
        strand: "number",
        steps: [{ taskId: "vs-ordering-bare", from: 5, to: 8 }],
        requires: ["order-by-size"],
    },
];
/**
 * Letters — the other half of the week.
 *
 * Every strand before this one was number, shape or thinking. A preschool and first-grade
 * teacher spends roughly half their week on the alphabet, and until the letters game there was
 * nothing on the assign screen for any of it.
 *
 * **Two orders run at right angles here, and keeping them apart is the design.** WITHIN a band
 * the letters arrive alphabetically — ا, then ب, then پ — because that is the order a class is
 * taught in and the one place in this bundle where a stimulus must not be drawn at random. The
 * BANDS themselves are ordered by what makes two letters hard to tell apart, which is a
 * different question and the only one an objective can honestly answer.
 *
 * The bands are deliberately NOT the textbook's order of introduction. A teacher following
 * فارسی اول ابتدایی meets the letters in a sequence built around the words they can spell, and
 * reproducing that sequence from memory — which is what writing it here would have meant —
 * would put an unverified claim about the national curriculum into the product's teaching
 * order. What these bands promise is only that each is harder than the last, and the game walks
 * the alphabet inside every one of them.
 *
 * The chain is linear because the difficulty genuinely is. Telling ب from پ is not a harder
 * version of telling ب from س — it is the next thing, and a child who cannot do the first
 * cannot be taught the second.
 */
const LETTERS = [
    {
        id: "letter-tell-apart",
        strand: "letters",
        steps: [{ taskId: "lit-letters-bare", from: 1, to: 2 }],
        requires: [],
    },
    {
        id: "letter-close-shapes",
        strand: "letters",
        steps: [{ taskId: "lit-letters-bare", from: 3, to: 4 }],
        requires: ["letter-tell-apart"],
    },
    /**
     * The dots, which are the Persian b/d problem and are worse than the Latin one.
     *
     * `mirror-shapes` already practises left/right discrimination in the abstract and says in its
     * own comment that it is there for b/d confusion. This is the concrete case, and Persian has
     * six letters on one skeleton where English has two: ب پ ت ث ن ی differ in nothing but where
     * the dots sit and how many there are. A child who cannot read the dots cannot read any of
     * the six.
     */
    {
        id: "letter-dots",
        strand: "letters",
        steps: [{ taskId: "lit-letters-bare", from: 5, to: 7 }],
        requires: ["letter-close-shapes"],
    },
    /**
     * The same letter in the shapes it wears inside a word.
     *
     * The step nothing else in the bundle can teach. A child who knows ب as a bowl with a dot
     * under it meets ـبـ in a word and has no reason to believe it is the same letter — the two
     * shapes share almost nothing to the eye. This is where a first-grade teacher loses weeks,
     * and it is the reason the letters game exists rather than the recognition levels below it.
     */
    {
        id: "letter-joined-forms",
        strand: "letters",
        steps: [{ taskId: "lit-letters-bare", from: 8, to: 11 }],
        requires: ["letter-dots"],
    },
    // The same question asked backwards, which is the direction reading actually needs.
    {
        id: "letter-name-the-form",
        strand: "letters",
        steps: [{ taskId: "lit-letters-bare", from: 12, to: 12 }],
        requires: ["letter-joined-forms"],
    },
    /**
     * Finding the letter in a word — where knowing a letter turns into using one.
     *
     * A second game rather than more levels on the first, because the question genuinely changes.
     * The alphabet game asks "which of these IS the letter"; this asks "which of these CONTAINS
     * it", and a child who can do the first often cannot do the second — the letter is smaller,
     * joined, and surrounded by others competing for the eye.
     *
     * It starts after `letter-tell-apart` rather than after the whole alphabet chain. Searching a
     * word for a shape needs the shape known and nothing else, so a teacher can hand this out in
     * the same week as the letter itself — which is how a class actually works.
     */
    {
        id: "find-letter-in-word",
        strand: "letters",
        steps: [{ taskId: "lit-find-letter-bare", from: 1, to: 3 }],
        requires: ["letter-tell-apart"],
    },
    // The wrong words start carrying letters on the target's own skeleton, so a glance stops working.
    {
        id: "find-letter-among-lookalikes",
        strand: "letters",
        steps: [{ taskId: "lit-find-letter-bare", from: 4, to: 6 }],
        requires: ["find-letter-in-word", "letter-dots"],
    },
    /**
     * The letter buried in the middle of a word, joined on both sides.
     *
     * Where it looks least like the letter the child learnt, and the case reading actually consists
     * of. Requires the joined forms from the other game for the obvious reason: a child who has not
     * met ـبـ cannot find it in a word.
     */
    {
        id: "find-letter-inside-word",
        strand: "letters",
        steps: [{ taskId: "lit-find-letter-bare", from: 7, to: 9 }],
        requires: ["find-letter-among-lookalikes", "letter-joined-forms"],
    },
    {
        id: "find-letter-in-long-word",
        strand: "letters",
        steps: [{ taskId: "lit-find-letter-bare", from: 10, to: 10 }],
        requires: ["find-letter-inside-word"],
    },
];
/**
 * Pattern — the strand that was asked for by name and already existed twice over.
 *
 * "Pattern recognition" arrived as a request for a new game. It was already two games: matching
 * a pattern to a copy, and working out what continues one. What it was missing was depth, so
 * the sequence game grew from five levels to ten and the new levels carry the two kinds of
 * pattern a preschool curriculum actually names — structured units (ABB, AABB) and growing ones
 * — which the five-level version could not express at all.
 */
const PATTERN = [
    {
        id: "pattern-repeat",
        strand: "pattern",
        steps: [{ taskId: "vs-sequence-bare", from: 1, to: 2 }],
        requires: [],
    },
    {
        id: "pattern-two-things",
        strand: "pattern",
        steps: [{ taskId: "vs-sequence-bare", from: 3, to: 5 }],
        requires: ["pattern-repeat"],
    },
    // ABB, AABB — a unit with a shape of its own, rather than one item taking turns.
    {
        id: "pattern-structured",
        strand: "pattern",
        steps: [{ taskId: "vs-sequence-bare", from: 6, to: 8 }],
        requires: ["pattern-two-things"],
    },
    // Each repetition one longer than the last: the first rule that is about change.
    {
        id: "pattern-growing",
        strand: "pattern",
        steps: [{ taskId: "vs-sequence-bare", from: 9, to: 10 }],
        requires: ["pattern-structured"],
    },
    /**
     * The colour world, and it comes FIRST in this strand.
     *
     * `pattern-repeat` requires nothing, and neither does this — but the recommender offers the
     * lowest-coverage entry objective in a strand, and a four-year-old reads a colour pattern a year
     * before a shape one. Naming them separately is what lets a teacher hand out the easier of the two
     * on purpose rather than discovering it by watching a child fail the other.
     */
    {
        id: "pattern-color-repeat",
        strand: "pattern",
        steps: [{ taskId: "vs-sequence-color-bare", from: 1, to: 2 }],
        requires: [],
    },
    {
        id: "pattern-color-three",
        strand: "pattern",
        steps: [{ taskId: "vs-sequence-color-bare", from: 3, to: 4 }],
        requires: ["pattern-color-repeat"],
    },
    // ABB and AABB, told entirely in colour.
    {
        id: "pattern-color-structured",
        strand: "pattern",
        steps: [{ taskId: "vs-sequence-color-bare", from: 5, to: 6 }],
        requires: ["pattern-color-three"],
    },
    {
        id: "pattern-color-hard",
        strand: "pattern",
        steps: [{ taskId: "vs-sequence-color-bare", from: 7, to: 8 }],
        requires: ["pattern-color-structured"],
    },
    /**
     * Carrying a motif along a strip — the pattern strand's only two-dimensional idea.
     *
     * It requires `pattern-repeat` rather than nothing, because a child who cannot yet see that a row
     * repeats has no reason to expect a block to. It does NOT require the structured or growing
     * bands: those make the unit more complicated along one axis, and this makes it taller, which is
     * a different thing to learn rather than a harder version of the same one.
     */
    {
        id: "carry-a-motif",
        strand: "pattern",
        steps: [{ taskId: "vs-tiling-bare", from: 1, to: 3 }],
        requires: ["pattern-repeat"],
    },
    {
        id: "carry-a-wider-motif",
        strand: "pattern",
        steps: [{ taskId: "vs-tiling-bare", from: 4, to: 6 }],
        requires: ["carry-a-motif"],
    },
    {
        id: "carry-a-full-motif",
        strand: "pattern",
        steps: [{ taskId: "vs-tiling-bare", from: 7, to: 8 }],
        requires: ["carry-a-wider-motif"],
    },
    {
        id: "pattern-match",
        strand: "pattern",
        steps: [{ taskId: "vs-pattern-match-bare", from: 1, to: 4 }],
        requires: [],
    },
    {
        id: "pattern-match-close",
        strand: "pattern",
        steps: [{ taskId: "vs-pattern-match-bare", from: 5, to: 8 }],
        requires: ["pattern-match"],
    },
];
/**
 * Logic — ruling things out, which no other strand asks for.
 *
 * Every other game asks a child to FIND something. These two ask them to eliminate: the shape
 * that cannot go in this square because it is already in the row, the command that cannot come
 * next because the robot would face a wall. It is the one strand where being wrong is
 * informative to the child rather than only to us.
 */
const LOGIC = [
    {
        id: "sudoku-shapes",
        strand: "logic",
        steps: [{ taskId: "logic-sudoku-bare", from: 1, to: 3 }],
        requires: [],
    },
    {
        id: "sudoku-shapes-deeper",
        strand: "logic",
        steps: [{ taskId: "logic-sudoku-bare", from: 4, to: 6 }],
        requires: ["sudoku-shapes"],
    },
    /**
     * The same puzzle with digits instead of shapes — its own objective, not a silent change.
     *
     * A child who can solve a shape sudoku and stalls on a digit one has not got worse at logic;
     * they cannot yet read the symbols. Two skills in one level would report the second as a
     * failure of the first, which is the confound `languageIndependentStimulus` exists to flag.
     */
    {
        id: "sudoku-digits",
        strand: "logic",
        steps: [{ taskId: "logic-sudoku-bare", from: 7, to: 10 }],
        requires: ["sudoku-shapes-deeper", "count-to-5"],
    },
    /**
     * Following a key — the one chain where the rule is handed over rather than worked out.
     *
     * Filed under logic beside sudoku and the robot because all three are about carrying a rule
     * through to the end, and unlike the other two this one does not ask the child to discover
     * anything. It is the entry point of the strand for exactly that reason: a four-year-old can do
     * "this shape is red" a year before they can rule anything out.
     */
    {
        id: "follow-a-key",
        strand: "logic",
        steps: [{ taskId: "vs-code-key-bare", from: 1, to: 2 }],
        requires: [],
    },
    {
        id: "follow-a-longer-key",
        strand: "logic",
        steps: [{ taskId: "vs-code-key-bare", from: 3, to: 5 }],
        requires: ["follow-a-key"],
    },
    /**
     * The key is read by counting, which is why it wants counting first.
     *
     * A child who can apply a shape key and stalls here has not got worse at following instructions;
     * they cannot yet read the quantity. Two skills in one band would report the second as a failure
     * of the first.
     */
    {
        id: "follow-a-counted-key",
        strand: "logic",
        steps: [{ taskId: "vs-code-key-bare", from: 6, to: 8 }],
        requires: ["follow-a-longer-key", "count-to-5"],
    },
    {
        id: "robot-basics",
        strand: "logic",
        steps: [{ taskId: "vs-robot-bare", from: 1, to: 3 }],
        requires: [],
    },
    {
        id: "robot-obstacles",
        strand: "logic",
        steps: [{ taskId: "vs-robot-bare", from: 4, to: 6 }],
        requires: ["robot-basics"],
    },
    {
        id: "robot-long-programs",
        strand: "logic",
        steps: [{ taskId: "vs-robot-bare", from: 7, to: 8 }],
        requires: ["robot-obstacles"],
    },
];
/**
 * Space — direction, reflection, and copying what you see.
 *
 * `mirror-shapes` is here for a specific reason worth writing down: telling a shape from its
 * reflection is the same left/right discrimination behind b/d confusion, and the year before
 * reading is when practising it is cheapest.
 */
const SPACE = [
    /**
     * Recognising a thing from its outline alone.
     *
     * First in the strand and requiring nothing, because it is the earliest of these a child can do:
     * a three-year-old matches a shadow to a toy. The three bands are not three difficulties of one
     * idea — they are what the row contains. The first asks whether a child can read an outline at
     * all; the second puts the answer's look-alikes beside it, so the outline stops deciding and the
     * detail has to; the third does that twice over on one screen.
     */
    {
        id: "match-a-shadow",
        strand: "space",
        steps: [{ taskId: "vs-shadow-bare", from: 1, to: 3 }],
        requires: [],
    },
    {
        id: "match-a-shadow-alike",
        strand: "space",
        steps: [{ taskId: "vs-shadow-bare", from: 4, to: 5 }],
        requires: ["match-a-shadow"],
    },
    {
        id: "match-a-shadow-crowd",
        strand: "space",
        steps: [{ taskId: "vs-shadow-bare", from: 6, to: 8 }],
        requires: ["match-a-shadow-alike"],
    },
    {
        id: "mirror-shapes",
        strand: "space",
        steps: [{ taskId: "vs-odd-one-out-bare", from: 2, to: 4 }],
        requires: [],
    },
    {
        id: "mirror-shapes-close",
        strand: "space",
        steps: [{ taskId: "vs-odd-one-out-bare", from: 5, to: 8 }],
        requires: ["mirror-shapes"],
    },
    /**
     * Seeing what a shape is made of.
     *
     * Three bands and only the first two are about size; the third is about colour. That split is
     * the game: once a figure has four pieces, adding a fifth mostly adds scanning, and the real next
     * step is a pile holding the right shapes in the wrong colours — which a child can only settle by
     * checking the inventory twice rather than by recognising a silhouette.
     */
    {
        id: "shape-from-pieces",
        strand: "space",
        steps: [{ taskId: "vs-parts-bare", from: 1, to: 3 }],
        requires: [],
    },
    {
        id: "shape-from-more-pieces",
        strand: "space",
        steps: [{ taskId: "vs-parts-bare", from: 4, to: 6 }],
        requires: ["shape-from-pieces"],
    },
    {
        id: "shape-from-pieces-colours",
        strand: "space",
        steps: [{ taskId: "vs-parts-bare", from: 7, to: 8 }],
        requires: ["shape-from-more-pieces"],
    },
    {
        id: "copy-shape",
        strand: "space",
        steps: [{ taskId: "vs-build-copy-bare", from: 2, to: 4 }],
        requires: [],
    },
    {
        id: "copy-shape-complex",
        strand: "space",
        steps: [{ taskId: "vs-build-copy-bare", from: 5, to: 8 }],
        requires: ["copy-shape"],
    },
    /**
     * Making a reflection, which is a different job from spotting one.
     *
     * Requires `mirror-shapes` rather than `copy-shape`, and the choice is the teaching order: a
     * child who cannot yet SEE that two shapes are reflections has nothing to aim at when asked to
     * make one. Copying is a lighter task and is not on the path.
     *
     * **The three bands are the game's two axes rather than three sizes of one thing.** The first is
     * where a spot goes and nothing else; the second adds a second colour, so a spot has to arrive in
     * the right place AND the right colour; the third runs on three. A child can hold the first and
     * lose the second, which is why a teacher gets to hand them out separately.
     */
    {
        id: "fold-a-shape",
        strand: "space",
        steps: [{ taskId: "vs-mirror-bare", from: 1, to: 3 }],
        requires: ["mirror-shapes"],
    },
    {
        id: "fold-a-shape-more",
        strand: "space",
        steps: [{ taskId: "vs-mirror-bare", from: 4, to: 6 }],
        requires: ["fold-a-shape"],
    },
    {
        id: "fold-a-shape-complex",
        strand: "space",
        steps: [{ taskId: "vs-mirror-bare", from: 7, to: 8 }],
        requires: ["fold-a-shape-more"],
    },
    {
        id: "find-a-way-out",
        strand: "space",
        steps: [{ taskId: "vs-maze-bare", from: 3, to: 4 }],
        requires: [],
    },
    {
        id: "find-a-way-out-larger",
        strand: "space",
        steps: [{ taskId: "vs-maze-bare", from: 5, to: 8 }],
        requires: ["find-a-way-out"],
    },
];
/**
 * Memory — holding an order, turning it around, and remembering where things were.
 *
 * `recall-order` has TWO steps, and it is the only objective that does. The same span task
 * exists in a bare version and a firefly version (the A-13 arms), so a child who will not touch
 * one can practise the objective through the other. That is what more than one step is for, and
 * this is the case that motivated allowing it.
 */
const MEMORY = [
    {
        id: "recall-order",
        strand: "memory",
        steps: [
            { taskId: "wm-span-forward-bare", from: 2, to: 4 },
            { taskId: "wm-span-forward-story", from: 2, to: 4 },
        ],
        requires: [],
    },
    {
        id: "recall-order-longer",
        strand: "memory",
        steps: [
            { taskId: "wm-span-forward-bare", from: 5, to: 9 },
            { taskId: "wm-span-forward-story", from: 5, to: 9 },
        ],
        requires: ["recall-order"],
    },
    {
        id: "recall-reversed",
        strand: "memory",
        steps: [{ taskId: "wm-span-backward-bare", from: 2, to: 4 }],
        requires: ["recall-order"],
    },
    {
        id: "recall-reversed-longer",
        strand: "memory",
        steps: [{ taskId: "wm-span-backward-bare", from: 5, to: 7 }],
        requires: ["recall-reversed"],
    },
    {
        id: "remember-where",
        strand: "memory",
        steps: [{ taskId: "wm-pairs-bare", from: 1, to: 4 }],
        requires: [],
    },
    {
        id: "remember-where-more",
        strand: "memory",
        steps: [{ taskId: "wm-pairs-bare", from: 5, to: 8 }],
        requires: ["remember-where"],
    },
];
/** Focus — noticing a small change, and noticing when the rule changed. */
const FOCUS = [
    {
        id: "spot-a-change",
        strand: "focus",
        steps: [{ taskId: "vs-difference-bare", from: 1, to: 4 }],
        requires: [],
    },
    {
        id: "spot-a-small-change",
        strand: "focus",
        steps: [{ taskId: "vs-difference-bare", from: 5, to: 8 }],
        requires: ["spot-a-change"],
    },
    /**
     * Sweeping a whole field rather than finding one thing in it.
     *
     * Its own strand entry rather than a band of `spot-a-change`, because the two ask for opposite
     * things: spotting a change is over the moment something is noticed, and this is not over until
     * everywhere has been looked at. A child can be good at one and poor at the other, and a teacher
     * wants to hand them out separately.
     */
    {
        id: "find-them-all",
        strand: "focus",
        steps: [{ taskId: "vs-search-bare", from: 1, to: 3 }],
        requires: [],
    },
    {
        id: "find-them-all-larger",
        strand: "focus",
        steps: [{ taskId: "vs-search-bare", from: 4, to: 6 }],
        requires: ["find-them-all"],
    },
    // The wrong arrows are now a quarter turn away, so scanning and telling apart are both loaded.
    {
        id: "find-them-all-close",
        strand: "focus",
        steps: [{ taskId: "vs-search-bare", from: 7, to: 8 }],
        requires: ["find-them-all-larger"],
    },
    {
        id: "notice-the-rule-changed",
        strand: "focus",
        steps: [{ taskId: "ef-rule-switch-bare", from: 1, to: 4 }],
        requires: [],
    },
    {
        id: "notice-the-rule-changed-often",
        strand: "focus",
        steps: [{ taskId: "ef-rule-switch-bare", from: 5, to: 8 }],
        requires: ["notice-the-rule-changed"],
    },
];
/**
 * Following a key that changes — the visible twin of noticing a hidden rule change.
 *
 * Filed in focus beside `notice-the-rule-changed`, and deliberately NOT after it. The two are not a
 * progression: one asks a child to work out that something moved, the other tells them and asks
 * them to act on it against a habit they have just built. A teacher may want either first, so
 * neither requires the other.
 */
const KEY_MAP = [
    /**
     * Runs through difficulty 4, not 3, so a child never confined to one objective still meets
     * every direction.
     *
     * Directions are numbered 0 up, 1 right, 2 down, 3 left, and a round only uses as many of them
     * as it has colours — two colours below difficulty 4. Stopping this objective at 3 kept a child
     * who never advanced past it (which `stepUpAfter`/`maxTrials` in `task-defs.ts` allow) inside
     * rounds that could ask for up, right or down, but never left.
     */
    {
        id: "follow-a-key-that-changes",
        strand: "focus",
        steps: [{ taskId: "ef-key-map-bare", from: 1, to: 4 }],
        requires: [],
    },
    {
        id: "follow-a-key-that-changes-often",
        strand: "focus",
        steps: [{ taskId: "ef-key-map-bare", from: 5, to: 6 }],
        requires: ["follow-a-key-that-changes"],
    },
    {
        id: "follow-a-key-that-barely-settles",
        strand: "focus",
        steps: [{ taskId: "ef-key-map-bare", from: 7, to: 8 }],
        requires: ["follow-a-key-that-changes-often"],
    },
];
export const OBJECTIVES_2_0_0 = [
    ...NUMBER,
    ...ORDERING,
    ...LETTERS,
    ...PATTERN,
    ...LOGIC,
    ...SPACE,
    ...MEMORY,
    ...FOCUS,
    ...KEY_MAP,
];
//# sourceMappingURL=objectives-2.0.0.js.map